import React from "react";
import { connect } from "react-redux";

import Dialog from "../core/components/dialog/Dialog";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import File from "../core/types/files/File";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";
import Import from "../core/components/import/Import";
import Info from "../core/components/info/Info";
import InputForm from "../core/components/inputform/InputForm";
import InputFormField from "../core/components/inputform/InputFormField";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Panel from "../core/components/panels/Panel";
import SelectList from "../core/components/inputform/SelectList";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { AppDispatch, IAppState } from "../core/state/Store";
import { DataSet } from "../core/types/data/DataSet";
import { DataTable } from "../core/types/data/DataTable";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import Faculty from "../core/types/core/Faculty";
import {
    archiveFaculty,
    importFacultiesFromTD,
    initialiseFacultyDetails,
    saveFaculty,
} from "../core/state/core/Faculties";
import { Alignment, Side } from "../core/types/system/Layout";
import {
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../core/types/data/SpreadsheetConfiguration";
import YearLevel from "../core/types/core/YearLevel";
import Toggle from "../core/components/inputform/Toggle";
import Text from "../core/components/text/Text";
import { addAlert } from "../core/state/system/Alerts";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import SortDialog from "../core/components/sorting/SortDialog";
import GridGrouping from "../core/components/grid/GridGrouping";
import Tabs from "../core/components/tabs/Tabs";
import TabContainer from "../core/components/tabs/TabContainer";
import SelectorList from "../core/components/selector/SelectorList";
import Utility from "../core/foundation/Utility";

import template from "../resources/Faculty_Import_Template.xlsx";

import "./Faculties.scss";

interface RouteFacultiesProps {
    archives: Faculty[];
    faculties: Faculty[];
    flat: Faculty[];
    yearLevels: YearLevel[];

    setPageLoading: (loading: boolean) => void;
    initialiseFacultyDetails: () => Promise<void>;
    saveFaculty: (details: Faculty[]) => Promise<void>;
    archiveFaculty: (details: Faculty[]) => Promise<void>;
}

interface RouteFacultiesState {
    archives: boolean;

    editOpen: boolean;
    editLoad: boolean;
    editTitle: string;
    editStep: number;
    editWidth: number;

    sorting: Faculty[];
    sortOpen: boolean;
    sortLoad: boolean;

    importOpen: boolean;
    importLoad: boolean;
    importType: string;
    importStep: number;
    importWidth: number;
    importValid: boolean;

    selection: Faculty | null;
    selections: Faculty[];

    editing: Faculty | null;
}

class RouteFaculties extends React.Component<
    RouteFacultiesProps,
    RouteFacultiesState
> {
    state: RouteFacultiesState = {
        archives: false,

        editOpen: false,
        editLoad: false,
        editTitle: "",
        editStep: 0,
        editWidth: 600,

        sorting: [],
        sortOpen: false,
        sortLoad: false,

        importOpen: false,
        importLoad: false,
        importType: undefined,
        importStep: 0,
        importWidth: 400,
        importValid: false,

        selection: null,
        selections: [],

        editing: null,
    };

    static getDerivedStateFromProps(
        props: RouteFacultiesProps,
        state: RouteFacultiesState,
    ) {
        return {
            importWidth:
                state.importStep === 0
                    ? state.importType === "csv"
                        ? 1000
                        : state.importType === "tdf"
                        ? 580
                        : 580
                    : 580,
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);

            await Promise.all([this.props.initialiseFacultyDetails()]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: new Faculty({
                id: 0,
                code: "",
            }),
            editOpen: true,
            editLoad: false,
            editTitle: "Add Faculty",
            editStep: 0,
        });
    }

    async edit() {
        if (!this.state.selection) return;

        try {
            this.props.setPageLoading(true);

            const temp = new Faculty(this.state.selection);
            this.setState({
                editing: temp,
                editOpen: true,
                editLoad: false,
                editTitle: "Edit Faculty",
                editStep: 0,
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    cancelEdit() {
        this.setState({ editing: null, editOpen: false });
    }

    canSave(): boolean {
        if (!this.state.editing) return false;

        return !!(this.state.editing.code && this.state.editing.name);
    }

    async saveEdit() {
        if (!this.state.editing) return;

        try {
            this.setState({ editLoad: true });

            await this.props.saveFaculty([this.state.editing]);
        } finally {
            this.setState({ editing: null, editLoad: false, editOpen: false });
        }
    }

    async rowEdit(record: Faculty, changes: Faculty) {
        if (!this.state.archives) {
            this.props.saveFaculty([new Faculty({ ...record, ...changes })]);
        }
    }

    async delete() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Archive Faculties",
            text: `Are you sure you want to archive the listed faculties?<br/><ul class="indented chevron"><li>${this.state.selections
                .map(item => item.display)
                .join("</li><li>")}</li></ul>`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.archiveFaculty(this.state.selections);

                this.setState({ selection: null, selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    sort() {
        this.setState({
            sorting: this.props.flat
                .map(faculty => new Faculty(faculty))
                .sort(Utility.getSorter([`order`])),
            sortOpen: true,
        });
    }

    async saveSort(sorted: Faculty[]) {
        try {
            this.setState({ sortLoad: true });

            for (const [newIndex, item] of sorted.entries())
                item.order = newIndex;

            await this.props.saveFaculty(sorted);

            this.setState({ sortOpen: false });
        } finally {
            this.setState({ sortLoad: false });
        }
    }

    import() {
        this.setState({
            importOpen: true,
            importStep: 0,
            importType: undefined,
        });
    }

    importSetType(type: string) {
        this.setState({ importType: type, importStep: 0 });
    }

    importSetStep(step: number) {
        this.setState({ importStep: step });
    }

    importNextStep() {
        this.setState({ importStep: this.state.importStep + 1 });
    }

    importLastStep() {
        this.setState({ importStep: this.state.importStep - 1 });
    }

    importProceedChanged(valid: boolean) {
        this.setState({ importValid: valid });
    }

    importCancel() {
        this.setState({ importOpen: false });
    }

    getSelectableFaculties(): Faculty[] {
        const main = this.props.faculties?.find(
            f => f.id === this.state?.editing?.id,
        );
        const ungrouped = this.props.faculties?.find(
            f => f.code === "[Ungrouped]",
        );

        return (ungrouped?.faculties ?? [])
            .concat(main?.faculties ?? [])
            .filter(c => c.id !== this.state.editing?.id);
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <Toggle
                        onLabel="Archives"
                        offLabel="Archives"
                        value={this.state.archives}
                        onChange={value =>
                            this.setState({ archives: value ?? false })
                        }
                    />
                    <ToolbarButton
                        icon="add"
                        text="Add"
                        tooltip="Add a new duty area"
                        onClick={this.add.bind(this)}
                    />
                    <ToolbarButton
                        icon="edit"
                        text="Edit"
                        tooltip={`Edit the selected duty area${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.edit.bind(this)}
                    />
                    <ToolbarButton
                        icon="archive"
                        text="Archive"
                        tooltip={`Archive the selected duty area${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.delete.bind(this)}
                    />
                    <ToolbarButton
                        icon="filter_list"
                        text="Sort"
                        tooltip="Sort all duty areas"
                        onClick={this.sort.bind(this)}
                    />
                    <ToolbarButton
                        icon="system_update_alt"
                        text="Import"
                        tooltip="Import rooms"
                        onClick={this.import.bind(this)}
                    />
                </MainToolbarContents>

                {(this.state.archives
                    ? this.props.archives?.length
                    : this.props.faculties?.length) > 0 ? (
                    <Grid<Faculty>
                        value={
                            this.state.archives
                                ? this.props.archives
                                : this.props.faculties
                        }
                        window={true}
                        collapsed={true}
                        fixedHeader={true}
                        keyField="id"
                        defaultSortField="order"
                        selectionChanged={(selection, selections) =>
                            this.setState({ selection, selections })
                        }
                        cellDoubleClicked={this.edit.bind(this)}
                        valueEdited={this.rowEdit.bind(this)}>
                        {!this.state.archives && (
                            <GridGrouping value="faculties">
                                <GridColumn
                                    value=""
                                    width="40px"
                                />
                                <GridColumn
                                    header="Code"
                                    field="code"
                                    selectable={true}
                                />
                                <GridColumn
                                    header="Name"
                                    field="name"
                                    selectable={true}
                                />
                            </GridGrouping>
                        )}
                        <GridColumn
                            header="No"
                            field="order"
                            selectable={true}
                            alignment={Alignment.Left}
                            width="40px"
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            header="Name"
                            field="name"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                    </Grid>
                ) : (
                    <Text clearanceSide={Side.All}>
                        {this.state.archives
                            ? "No archived faculties found."
                            : "No active faculties found."}
                    </Text>
                )}

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={this.state.editWidth}>
                    <DialogContent>
                        <Tabs
                            boxed={true}
                            onTabChange={tab =>
                                this.setState({
                                    editWidth: tab === "Faculties" ? 900 : 600,
                                })
                            }>
                            <TabContainer header="Faculty Details">
                                <Panel>
                                    <InputForm
                                        binding={this.state.editing}
                                        display={RenderOrientations.Vertical}
                                        fieldDisplay={
                                            RenderOrientations.Vertical
                                        }
                                        onChange={value =>
                                            this.setState({ editing: value })
                                        }>
                                        <InputFormField
                                            label="Code"
                                            field="code"
                                            mandatory={true}
                                        />
                                        <InputFormField
                                            label="Name"
                                            field="name"
                                            mandatory={true}
                                        />
                                    </InputForm>
                                </Panel>
                            </TabContainer>
                            <TabContainer
                                header="Faculties"
                                hidden={this.state.editing?.isChild}>
                                <Panel>
                                    <SelectorList
                                        keyField="id"
                                        source={this.getSelectableFaculties()}
                                        value={
                                            this.state?.editing?.faculties ?? []
                                        }
                                        onChange={value =>
                                            this.setState({
                                                editing: new Faculty({
                                                    ...this.state.editing,
                                                    faculties: value,
                                                }),
                                            })
                                        }>
                                        <GridColumn
                                            header="Code"
                                            field="code"
                                        />
                                        <GridColumn
                                            header="Name"
                                            field="name"
                                        />
                                    </SelectorList>
                                </Panel>
                            </TabContainer>
                        </Tabs>

                        <InputFormMandatory />
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            onClick={this.saveEdit.bind(this)}
                            disabled={!this.canSave()}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.importOpen}
                    showLoading={this.state.importLoad}
                    dialogIcon="system_update_alt"
                    dialogTitle="Import"
                    canResize={false}
                    width={this.state.importWidth}>
                    <DialogContent>
                        <ConnectedImportWizard
                            importType={this.state.importType}
                            importStep={this.state.importStep}
                            setType={this.importSetType.bind(this)}
                            setStep={this.importSetStep.bind(this)}
                            validChanged={this.importProceedChanged.bind(this)}
                            setLoading={value =>
                                this.setState({ importLoad: value })
                            }
                        />
                    </DialogContent>
                    <DialogFooter>
                        {this.state.importStep !== 1 && (
                            <DialogCancel
                                text={
                                    this.state.importStep < 1
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.importCancel.bind(this)}
                            />
                        )}
                        {this.state.importStep < 1 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.state.importValid}
                                onClick={this.importNextStep.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                <SortDialog<Faculty>
                    keyField="id"
                    value={this.state.sorting}
                    showDialog={this.state.sortOpen}
                    showLoading={this.state.sortLoad}
                    onSave={this.saveSort.bind(this)}
                    onCancel={() => this.setState({ sortOpen: false })}>
                    <GridColumn
                        header="No"
                        field="order"
                        alignment={Alignment.Left}
                        width="40px"
                    />
                    <GridColumn
                        header="Code"
                        field="code"
                    />
                    <GridColumn
                        header="Name"
                        field="name"
                    />
                </SortDialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { faculty, campus, yearLevel } = state.db;
    return {
        campuses: campus.campuses,
        archives: faculty.archives,
        faculties: faculty.faculties,
        flat: faculty.flat,
        yearLevels: yearLevel.yearLevels,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseFacultyDetails: () =>
            unwrappedDispatch(initialiseFacultyDetails()),
        saveFaculty: (details: Faculty[]) =>
            unwrappedDispatch(saveFaculty(details)),
        archiveFaculty: (details: Faculty[]) =>
            unwrappedDispatch(archiveFaculty(details)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteFaculties);

interface ImportWizardProps {
    faculties: Faculty[];
    importType?: string;
    importStep: number;

    saveFaculty: (details: Faculty[]) => Promise<Faculty[]>;
    setType?: (type: string) => void;
    setStep?: (step: number) => void;
    setLoading?: (loading: boolean) => void;
    validChanged?: (valid: boolean) => void;
}

interface ImportWizardState {
    importData?: DataSet;
    importFile?: File;
    importLogs?: ImportLog;
}

class ImportWizard extends React.Component<
    ImportWizardProps,
    ImportWizardState
> {
    state: ImportWizardState = {};

    private importTypes = [
        { value: "csv", text: "XLSX/CSV", order: 1 },
        { value: "tdf", text: "TD File", order: 2 },
    ];

    private importConfig: SpreadsheetConfiguration = {
        Name: "Faculties",
        Tables: [
            {
                Name: "Faculties",
                Columns: [
                    { PK: true, Field: "code", Name: "Code" },
                    { Field: "name", Name: "Name" },
                    { Field: "group", Name: "Faculty Group Code" },
                ],
            },
        ],
    };

    componentDidMount(): void {
        this.setState({
            importData: undefined,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<ImportWizardProps>,
        prevState: Readonly<ImportWizardState>,
        snapshot?: any,
    ): void {
        if (prevProps.importType !== this.props.importType) {
            this.setState({
                importData: undefined,
                importFile: undefined,
            });

            this.props.validChanged?.(false);
        }

        if (prevProps.importStep !== this.props.importStep) {
            this.props.validChanged?.(this.importCanProceed());

            this.importProceed();
        } else if (prevState !== this.state) {
            this.props.validChanged?.(this.importCanProceed());
        }
    }

    importCanProceed(): boolean {
        switch (this.props.importStep) {
            case 0:
                switch (this.props.importType) {
                    case "csv":
                        return this.state.importData !== undefined;
                    case "tdf":
                        return this.state.importFile !== undefined;
                    default:
                        return false;
                }
            default:
                return false;
        }
    }

    importProceed() {
        switch (this.props.importStep) {
            case 1:
                switch (this.props.importType) {
                    case "csv":
                        this.doCsvImport();
                        break;
                    case "tdf":
                        this.doTDImport();
                        break;
                }
                break;
        }
    }

    async doCsvImport() {
        try {
            this.props.setStep?.(1);

            const adds = [];
            const upd = [];
            const groups = new Map<Faculty, Faculty>();
            const logs: ImportLog = {
                upsertCounts: [],
                skippedItems: [],
            };

            for (const config of this.importConfig.Tables) {
                const table = this.state.importData[config.Name];
                if (!table) continue;

                this.processCsvImport(config, table, adds, upd, groups, logs);
            }

            const results = await this.props.saveFaculty(adds.concat(upd));

            if (groups.size > 0) {
                upd.length = 0;
                for (const [faculty, group] of groups.entries()) {
                    const workingGroup =
                        upd.find(r => r.id === group.id) ??
                        results.find(r => r.id === group.id) ??
                        group;
                    const workingFaculty =
                        results.find(r => r.code === faculty.code) ?? faculty;
                    const updated = new Faculty({
                        ...workingGroup,
                        faculties: (workingGroup.faculties ?? []).concat(
                            workingFaculty,
                        ),
                    });

                    if (upd.some(u => u.id === updated.id)) upd.remove(updated);
                    upd.push(updated);
                }
                await this.props.saveFaculty(upd);
            }

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    processCsvImport(
        type: SpreadsheetTableDefinition,
        data: DataTable,
        adds: Faculty[],
        upd: Faculty[],
        groups: Map<Faculty, Faculty>,
        logs: ImportLog,
    ): void {
        for (const row of data) {
            const pk = type.Columns.find(c => c.PK)?.Field;
            if (!pk) continue;

            const pkVal = row[pk]?.toString().trim();
            if (!pkVal) continue;

            let faculty = this.props.faculties?.find(
                u => u[pk]?.toLowerCase() === pkVal.toLowerCase(),
            );

            if (!faculty) {
                faculty = new Faculty({
                    [pk]: pkVal,
                    order: this.props.faculties.map(f => f.order).max() + 1,
                });

                adds.push(faculty);
            } else {
                faculty = new Faculty({ ...faculty });
                if (!upd.includes(faculty)) upd.push(faculty);
            }

            for (const column of type.Columns) {
                if (column.PK) continue;

                const value = row[column.Field]?.toString().trim();
                if (!value) continue;

                switch (column.Field) {
                    case "group":
                        const group = this.props.faculties?.find(
                            f =>
                                f.code.toLowerCase() === value.toLowerCase() &&
                                f.id !== faculty.id &&
                                f.id !== -1 &&
                                !f.isChild,
                        );
                        // if (group) {
                        //     const existing = upd.find(u => u.id === group!.id);
                        //     if (!!existing) upd.remove(existing);
                        //     const updated = new Faculty({
                        //         ...(existing ?? group),
                        //     });
                        //     upd.push(updated);
                        //     updated.faculties.push(faculty);
                        // }
                        if (!!group) groups.set(faculty, group);
                        break;
                    default:
                        Reflect.set(
                            faculty,
                            column.Field as keyof Faculty,
                            value,
                        );
                        break;
                }
            }
        }

        logs.upsertCounts.push({
            name: type.Name,
            insertCount: adds.length,
            updateCount: upd.length,
            archiveCount: 0,
        });
    }

    async doTDImport() {
        try {
            this.props.setStep?.(1);

            const logs = await importFacultiesFromTD(this.state.importFile.id);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    render() {
        return (
            <>
                <SelectList
                    tabs={true}
                    value={this.props.importType}
                    options={this.importTypes}
                    onChange={value =>
                        this.props.importStep === 0 &&
                        this.props.setType?.(value)
                    }
                />

                <Wizard
                    flow={true}
                    frameless={true}
                    transparent={true}
                    clearanceSide={Side.Top}
                    step={this.props.importStep}>
                    <WizardStep step={0}>
                        {(() => {
                            switch (this.props.importType) {
                                case "csv":
                                    return (
                                        <Import
                                            config={this.importConfig}
                                            template={template}
                                            templateName={template.stripReactName()}
                                            onReady={(_, data) =>
                                                this.setState({
                                                    importData: data,
                                                })
                                            }
                                        />
                                    );
                                case "tdf":
                                    return (
                                        <FileExplorer
                                            type={FileExplorerTypes.Timetables}
                                            value={this.state.importFile}
                                            onChange={item =>
                                                this.setState({
                                                    importFile: item,
                                                })
                                            }
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={1}
                        framed={true}
                        hiddenWhenOffscreen={true}>
                        <div className="buffer text-center">
                            <h2>We're currently importing your data</h2>
                        </div>
                        <div className="process-indicator">
                            <div className="cube cube1"></div>
                            <div className="cube cube2"></div>
                            <div className="cube cube3"></div>
                            <div className="cube cube4"></div>
                            <div className="cube cube5"></div>
                            <div className="cube cube6"></div>
                            <div className="cube cube7"></div>
                            <div className="cube cube8"></div>
                            <div className="cube cube9"></div>
                            <div className="cube cube10"></div>
                            <div className="cube cube11"></div>
                            <div className="document1"></div>
                            <div className="document2"></div>
                        </div>
                    </WizardStep>
                    <WizardStep
                        step={2}
                        hiddenWhenOffscreen={true}>
                        <Panel>
                            <Panel
                                title="Addition/Update Counts"
                                bufferSide={Side.All}>
                                {this.state?.importLogs?.upsertCounts?.length >
                                0 ? (
                                    <Grid<UpsertCount>
                                        value={
                                            this.state.importLogs
                                                ?.upsertCounts ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Added"
                                            field="insertCount"
                                        />
                                        <GridColumn
                                            header="Updated"
                                            field="updateCount"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>
                                        No records were added or updated.
                                    </Info>
                                )}
                            </Panel>

                            <Panel
                                title="Skipped Items"
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}>
                                {this.state?.importLogs?.skippedItems?.length >
                                0 ? (
                                    <Grid<SkippedRecordCount>
                                        value={
                                            this.state.importLogs
                                                ?.skippedItems ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Number"
                                            field="count"
                                        />
                                        <GridColumn
                                            header="Reason"
                                            field="reason"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>No records were skipped.</Info>
                                )}
                            </Panel>
                        </Panel>
                    </WizardStep>
                </Wizard>
            </>
        );
    }
}

const mapStateToPropsWizard = (state: IAppState) => {
    const { faculty } = state.db;
    return {
        faculties: faculty.faculties,
    };
};

const mapDispatchToPropsWizard = (_dispatch: AppDispatch) => {
    return {
        saveFaculty: (details: Faculty[]) =>
            unwrappedDispatch(saveFaculty(details)),
    };
};

const ConnectedImportWizard = connect(
    mapStateToPropsWizard,
    mapDispatchToPropsWizard,
)(ImportWizard);
